import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  useReactTable,
} from "@tanstack/react-table";
import format from "date-fns/format";

import { LongTagList } from "@/components/LongTagList";
import { MoneyBadge } from "@/components/MoneyBadge";
import { Page } from "@/components/Page";
import { PayoutsPaymentsSwitcher } from "@/components/PayoutsPaymentsSwitcher";
import { Spinner } from "@/components/Spinner";
import { Avatar } from "@/components/ui/avatar";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNextLink,
  PaginationPreviousLink,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { H1 } from "@/components/ui/typography";
import {
  getHydratedUserDidQueryOptions,
  PaymentHistoryOptions,
  PayoutHistoryItem,
  payoutsHistoryQueryOptions,
} from "@/lib/api";
import { User } from "@/lib/types";
import { cn } from "@/lib/utils";

function UserCell({ username }: { username: string }) {
  const { data: user } = useQuery(getHydratedUserDidQueryOptions(username));

  if (!user) {
    return <Spinner delay={1000} />;
  }

  return (
    <div className="flex gap-2 items-center">
      <Avatar size="sm" src={user.avatar} alt={user.handle} />
      <div className="flex flex-col gap-0.5">
        <div>{user.display_name}</div>
        <a
          className="text-slate-dim text-xs"
          href={`https://bsky.app/profile/${user.handle}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          @{user.handle}
        </a>
      </div>
    </div>
  );
}

const columns: ColumnDef<PayoutHistoryItem>[] = [
  {
    header: "Date",
    cell: ({ row }) => format(new Date(row.original.created), "MM/dd"),
    size: 40,
  },
  {
    header: "Amount",
    size: 40,
    cell: ({ row }) => {
      return (
        <MoneyBadge
          amount={row.original.amount}
          currency={row.original.currency}
          variant="positive"
        />
      );
    },
  },
  {
    header: "Paid by",
    minSize: 200,
    maxSize: 1000,
    cell: ({ row }) => (
      <UserCell username={row.original.paying_user.username} />
    ),
  },
  {
    header: "Feeds",
    cell: ({ row }) => {
      if (!row.original.algorithms) {
        return null;
      }

      return <LongTagList tags={row.original.algorithms} />;
    },
  },
  {
    header: "Campaigns",
    cell: ({ row }) => {
      if (!row.original.campaigns) {
        return null;
      }

      return <LongTagList tags={row.original.campaigns} />;
    },
  },
];

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    user: User | undefined;
  }
}

export const Route = createFileRoute("/app/_sidebar-layout/payouts/history")({
  component: HomeComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      cursor: search?.cursor,
    } as PaymentHistoryOptions;
  },
  loader: ({ context: { queryClient }, location }) => {
    void Promise.all([
      queryClient.prefetchQuery(
        payoutsHistoryQueryOptions(location.search as PaymentHistoryOptions),
      ),
    ]);
  },
  head: () => ({ meta: [{ title: "Payments History" }] }),
});

function CampaignTables() {
  const routeParams = Route.useSearch();
  const { data, isPending, isFetching } = useQuery(
    payoutsHistoryQueryOptions(routeParams),
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {},
    columnResizeMode: "onEnd",
  });

  if (isPending) {
    return <Spinner delay={1000} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className={cn(isFetching && "opacity-50")}>
        {data?.data && data.data.length > 0 ? (
          <>
            <div className="md:hidden border border-slate-6 dark:border-slatedark-6 rounded-lg">
              {table.getRowModel().rows.map((row) => (
                <div
                  key={row.original.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="flex flex-col gap-4 p-4 not-last:border-b border-slate-6 dark:border-slatedark-6"
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="text-sm font-medium">
                      {format(new Date(row.original.created), "MM/dd")}
                    </div>
                    <div className="text-sm font-medium">
                      <MoneyBadge
                        amount={row.original.amount}
                        currency={row.original.currency}
                        variant="positive"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium">Paid by</div>
                    <UserCell username={row.original.paying_user.username} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium">Feeds</div>
                    <LongTagList tags={row.original.algorithms} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium">Campaigns</div>
                    <LongTagList tags={row.original.campaigns} />
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block border border-slate-6 dark:border-slatedark-6 rounded-lg">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className="whitespace-pre"
                          style={{ width: header.getSize() || 1000 }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.original.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="group"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="whitespace-pre">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <div className="text-slate-dim py-4">You have no payouts.</div>
        )}
      </div>
      {data?.data && (
        <Pagination className="w-full">
          <PaginationContent className="w-full justify-between">
            <PaginationItem>
              <PaginationPreviousLink
                to="/app/campaigns/history"
                search={(p) => ({
                  ...p,
                  cursor: data.previous_cursor ?? undefined,
                })}
                disabled={!data.previous_cursor}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationNextLink
                to="/app/campaigns/history"
                search={(p) => ({
                  ...p,
                  cursor: data.next_cursor ?? undefined,
                })}
                disabled={!data.next_cursor}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

function HomeComponent() {
  return (
    <Page className="md:gap-10">
      <H1>Payouts History</H1>
      <div className="flex flex-col gap-4 w-full">
        <PayoutsPaymentsSwitcher value="payouts" />
        <CampaignTables />
      </div>
    </Page>
  );
}
